import { onBeforeUnmount, shallowRef } from 'vue'
import { userPost } from './request.js'
import { getForumSession, getForumProfile, captureForumSession, isCurrentForumSession, staleForumSessionError } from './forumSession.js'
import { registerMeetingSessionGuard } from './meetingSessionGuard.js'
import { createMeetingAccountAccess } from './meetingAccountAccess.js'
import { redirectToMeetingLogin } from './meetingLoginRedirect.js'

export const meetingAccountNotice = shallowRef(null)

export function useMeetingAccount({ role, getForumId, isConnected, canResumeConnection, onChange, onVerified, leave }) {
  let unregister
  const owner = {}
  const access = createMeetingAccountAccess({
    session: getForumSession(), isConnected, canResumeConnection, onChange,
    verify: async () => {
      const expected = captureForumSession()
      const moderator = role === 'moderator'
      const response = await userPost(moderator ? '/matrix-search/forum/employee/info' : '/matrix-search/forum/joinHCMeeting',
        moderator ? {} : { forumId: getForumId() }, { sessionCheck: true })
      const data = response?.result || response?.data || {}
      if (!isCurrentForumSession(expected)) throw staleForumSessionError()
      const success = [0, 200].includes(Number(response?.code))
      const allowed = success && (moderator ? data.isEmployee === true : data.joinPermitFlag === true && Number(data.meetingStatus) !== 6)
      const employee = data.employeeInfo ?? data.employee ?? data.userInfo ?? data.user ?? data
      // Only a server-issued stable ID can establish identity after token renewal.
      // No guessing from a display name, masked phone or an SDK role.
      const id = moderator ? employee.employeeId ?? employee.userId ?? employee.id : data.userId ?? data.uid
      if (moderator && allowed) {
        sessionStorage.setItem('meetingOperator', JSON.stringify({
          operatorId: id || '', operatorName: employee.employeeName ?? employee.userName ?? employee.name ?? '',
        }))
      }
      const profile = getForumProfile()
      return { allowed, identity: id == null ? '' : `${role}:${id}`,
        // Shared profile is display-only. Identity/permission above must come from the API.
        accountName: employee.employeeName || employee.userName || employee.name ||
          profile?.nameShow || profile?.uf?.nickname || profile?.uf?.username || '', data }
    },
    onState: state => {
      if (state.status === 'anonymous') {
        if (meetingAccountNotice.value?.owner === owner) meetingAccountNotice.value = null
        redirectToMeetingLogin()
      } else if (state.status === 'ready') {
        if (meetingAccountNotice.value?.owner === owner) meetingAccountNotice.value = null
        onVerified?.(state.result.data)
      } else {
        meetingAccountNotice.value = { ...state, owner, retry: () => access.retry(), leave }
      }
    },
  })
  onBeforeUnmount(() => {
    access.dispose()
    unregister?.()
    if (meetingAccountNotice.value?.owner === owner) meetingAccountNotice.value = null
  })
  return {
    allowed: access.allowed,
    start: () => {
      if (!unregister) unregister = registerMeetingSessionGuard(access)
      return access.start()
    },
  }
}
